export const DEFAULT_LANGUAGE='es-419';
export const SUPPORTED_LANGUAGES=Object.freeze(['es-419','en']);
export const LANGUAGE_STORAGE_KEY='turbo-crown-language';

export function normalizeLanguage(value){
  const language=String(value||'').trim().toLowerCase();
  if(language==='en'||language.startsWith('en-'))return 'en';
  return DEFAULT_LANGUAGE;
}

function languageFromUrl(location){
  try{return normalizeLanguage(new URL(location.href).searchParams.get('lang'));}
  catch{return DEFAULT_LANGUAGE;}
}

function storedLanguage(storage){
  try{return normalizeLanguage(storage?.getItem(LANGUAGE_STORAGE_KEY));}
  catch{return DEFAULT_LANGUAGE;}
}

function splitWhitespace(value){
  const match=String(value??'').match(/^(\s*)([\s\S]*?)(\s*)$/);
  return {before:match?.[1]||'',content:match?.[2]||'',after:match?.[3]||''};
}

export function createI18n({
  translations={},
  rules=[],
  initialLanguage,
  storage=globalThis.localStorage,
  location=globalThis.location,
  syncUrl=false,
}={}){
  const textSources=new WeakMap();
  const textRendered=new WeakMap();
  const attributeSources=new WeakMap();
  const attributeRendered=new WeakMap();
  const translatedAttributes=['aria-label','placeholder','title','alt','content'];
  let urlLanguage='';
  try{
    const requested=location&&new URL(location.href).searchParams.get('lang');
    if(requested)urlLanguage=normalizeLanguage(requested);
  }catch{}
  let language=normalizeLanguage(initialLanguage||urlLanguage||storedLanguage(storage));

  function translated(source){
    if(language===DEFAULT_LANGUAGE)return source;
    const exact=translations[language]?.[source];
    if(exact!==undefined)return exact;
    for(const [pattern,replacement] of rules[language]||[]){
      pattern.lastIndex=0;
      if(pattern.test(source)){
        pattern.lastIndex=0;
        return source.replace(pattern,replacement);
      }
    }
    return source;
  }

  function renderTextNode(node,{capture=false}={}){
    const current=node.nodeValue||'';
    if(capture||!textSources.has(node))textSources.set(node,current);
    const source=textSources.get(node)||'';
    const {before,content,after}=splitWhitespace(source);
    const next=`${before}${translated(content)}${after}`;
    textRendered.set(node,next);
    if(current!==next)node.nodeValue=next;
  }

  function attributeMaps(element){
    if(!attributeSources.has(element))attributeSources.set(element,new Map());
    if(!attributeRendered.has(element))attributeRendered.set(element,new Map());
    return {
      sources:attributeSources.get(element),
      rendered:attributeRendered.get(element),
    };
  }

  function renderAttribute(element,name,{capture=false}={}){
    if(!element.hasAttribute?.(name))return;
    const current=element.getAttribute(name)||'';
    const {sources,rendered}=attributeMaps(element);
    if(capture||!sources.has(name))sources.set(name,current);
    const next=translated(sources.get(name)||'');
    rendered.set(name,next);
    if(current!==next)element.setAttribute(name,next);
  }

  function translateTree(root=document,{capture=false}={}){
    if(root.nodeType===Node.TEXT_NODE){
      renderTextNode(root,{capture});
      return;
    }
    if(root.nodeType!==Node.ELEMENT_NODE&&root.nodeType!==Node.DOCUMENT_NODE&&root.nodeType!==Node.DOCUMENT_FRAGMENT_NODE)return;
    if(root.nodeType===Node.ELEMENT_NODE){
      const tag=root.tagName;
      if(tag==='SCRIPT'||tag==='STYLE'||tag==='NOSCRIPT')return;
      translatedAttributes.forEach((name)=>renderAttribute(root,name,{capture}));
    }
    const walker=document.createTreeWalker(root,NodeFilter.SHOW_TEXT|NodeFilter.SHOW_ELEMENT,{
      acceptNode(node){
        if(node.nodeType===Node.ELEMENT_NODE&&['SCRIPT','STYLE','NOSCRIPT'].includes(node.tagName))return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let node=walker.nextNode();
    while(node){
      if(node.nodeType===Node.TEXT_NODE)renderTextNode(node,{capture});
      else translatedAttributes.forEach((name)=>renderAttribute(node,name,{capture}));
      node=walker.nextNode();
    }
  }

  function updateDocumentLanguage(){
    document.documentElement.lang=language;
    document.documentElement.dataset.language=language;
  }

  function updateUrl(){
    if(!syncUrl||!location||!globalThis.history?.replaceState)return;
    const url=new URL(location.href);
    if(language===DEFAULT_LANGUAGE)url.searchParams.delete('lang');
    else url.searchParams.set('lang',language);
    globalThis.history.replaceState(globalThis.history.state,'',url);
  }

  function updateInternalLinks(){
    if(!syncUrl||!location)return;
    document.querySelectorAll('a[href]').forEach((link)=>{
      const raw=link.getAttribute('href');
      if(!raw||raw.startsWith('mailto:')||raw.startsWith('tel:')||raw.startsWith('javascript:'))return;
      try{
        const url=new URL(raw,location.href);
        if(url.origin!==location.origin)return;
        if(language===DEFAULT_LANGUAGE)url.searchParams.delete('lang');
        else url.searchParams.set('lang',language);
        link.href=`${url.pathname}${url.search}${url.hash}`;
      }catch{}
    });
  }

  function setLanguage(next,{persist=true}={}){
    language=normalizeLanguage(next);
    if(persist){
      try{storage?.setItem(LANGUAGE_STORAGE_KEY,language);}catch{}
    }
    updateDocumentLanguage();
    translateTree(document);
    updateUrl();
    updateInternalLinks();
    globalThis.dispatchEvent?.(new CustomEvent('turbo-crown:language-change',{detail:{language}}));
    return language;
  }

  const observer=new MutationObserver((mutations)=>{
    for(const mutation of mutations){
      if(mutation.type==='characterData'){
        const rendered=textRendered.get(mutation.target);
        if(mutation.target.nodeValue===rendered)continue;
        renderTextNode(mutation.target,{capture:true});
      }else if(mutation.type==='attributes'){
        const {rendered}=attributeMaps(mutation.target);
        if(mutation.target.getAttribute(mutation.attributeName)===rendered.get(mutation.attributeName))continue;
        renderAttribute(mutation.target,mutation.attributeName,{capture:true});
      }else{
        mutation.addedNodes.forEach((node)=>translateTree(node,{capture:true}));
      }
    }
    updateInternalLinks();
  });

  updateDocumentLanguage();
  translateTree(document,{capture:true});
  updateUrl();
  updateInternalLinks();
  observer.observe(document.documentElement,{
    subtree:true,
    childList:true,
    characterData:true,
    attributes:true,
    attributeFilter:translatedAttributes,
  });

  return {
    get language(){return language;},
    setLanguage,
    translate:(source)=>translated(String(source??'')),
    refresh:()=>translateTree(document),
    destroy:()=>observer.disconnect(),
  };
}
